import { fmtRelative } from "@/lib/format";

import type { TFunction } from "i18next";

type T = TFunction;

/** A 0..1 model score in words: never a raw "(0.93)" on a customer screen. */
export function likelihood(score: number, t: T): string {
  if (score >= 0.85) return t("approvals.likelihood.veryLikely");
  if (score >= 0.6) return t("approvals.likelihood.likely");
  return t("approvals.likelihood.possibly");
}

/** Reasons the worker writes (English, technical) → plain words in the reader's language. */
const KNOWN: [RegExp, `approvals.reasons.${string}`][] = [
  [/^negative sentiment$/i, "approvals.reasons.negative"],
  [/^positive sentiment$/i, "approvals.reasons.positive"],
  [/^spam$/i, "approvals.reasons.spam"],
  [/^hate$/i, "approvals.reasons.hate"],
  [/^complaint$/i, "approvals.reasons.complaint"],
  [/^question$/i, "approvals.reasons.question"],
  [/^price( question)?$/i, "approvals.reasons.price"],
  [/^dm agent suggestion$/i, "approvals.reasons.agent"],
  [/^contains a promotion$/i, "approvals.reasons.promotion"],
  [/^you asked the assistant to clean up spam$/i, "approvals.reasons.assistantSpam"],
];

/**
 * "Negative sentiment (0.93)" → "Negative comment, very likely"; "Spam (0.88) · contains a promotion" →
 * "Spam, very likely · Contains a promotion". Unknown phrases are kept as written, minus the raw score.
 */
export function reasonInWords(reason: string, t: T): string {
  return reason
    .split(/\s*·\s*/)
    .map((part) => {
      const m = /^(.*?)\s*\((0(?:\.\d+)?|1(?:\.0+)?)\)\s*$/.exec(part);
      const label = (m ? m[1]! : part).trim();
      const known = KNOWN.find(([re]) => re.test(label));
      const words = known ? t(known[1] as "approvals.reasons.spam") : label;
      return m ? t("approvals.reasons.withLikelihood", { reason: words, likelihood: likelihood(Number(m[2]), t) }) : words;
    })
    .filter(Boolean)
    .join(" · ");
}

/** Why Approve is off for a DM: "Instagram closed this conversation 2 days ago. Reply when they write again." */
export function windowClosedFor(expiresAt: string | null, t: T, now = Date.now()): string {
  return expiresAt ? t("approvals.card.windowClosedAgo", { when: fmtRelative(expiresAt, undefined, now) }) : t("approvals.card.windowClosedNow");
}
